import React, { useCallback, useEffect, useState } from "react";
import { Select, TextInput, Textarea } from "flowbite-react";
import { toast } from "react-toastify";
import { Spinner } from "../../atoms/Spinner";
import { StatusBadge } from "../../atoms/StatusBadge";
import { ModalShell } from "../../atoms/ModalShell";
import { CustomButton } from "../../atoms/CustomButton";
import { FieldLabel } from "../../atoms/FieldLabel";
import HttpService from "../../../services/http";
import ChatUpstreamPicker from "./ChatUpstreamPicker";

type PingResult = {
  provider: string;
  model: string;
  ok: boolean;
  latency_ms: number;
  answer?: string;
  error?: string;
};
type PingReport = {
  all_ok: boolean;
  total: number;
  ok_count: number;
  results: PingResult[];
};

type Model = {
  name: string;
  company: string;
  model: string;
  scope?: string;
  max_token?: number | null;
  max_output_token?: number | null;
  alternative?: string | null;
  params?: Record<string, unknown>;
};

type Tiers = Record<string, Record<string, string>>;

/** One chat configuration as the AI Engine reports it (/admin/chat). */
type ChatScenario = {
  scenario: string;
  bound: boolean;
  model_name?: string;
  pinned_upstream?: string | null;
  fallback?: string | null;
  price_in_per_1m?: number | null;
  price_out_per_1m?: number | null;
};

/** A provider account. `configured` only — the engine never returns the secret. */
type Credential = {
  provider: string;
  configured: boolean;
  updated_at: string | null;
};

const CHAT_ROLES: Array<{ key: string; label: string; hint: string }> = [
  { key: "smartest", label: "Smartest", hint: "Hardest questions" },
  { key: "smart", label: "Smart", hint: "Default quality" },
  { key: "fast", label: "Fast", hint: "Quick answers" },
  { key: "fastest", label: "Fastest", hint: "Lowest latency" },
  { key: "cheapest", label: "Cheapest", hint: "Corrections, summaries" },
];

const VOICE_ROLES: Array<{ key: string; label: string; hint: string }> = [
  {
    key: "tts_quality",
    label: "Text to speech (quality)",
    hint: "Most natural, slower",
  },
  { key: "tts_fast", label: "Text to speech (fast)", hint: "Lower latency" },
  {
    key: "stt_quality",
    label: "Speech to text (quality)",
    hint: "Best transcription",
  },
  { key: "stt_fast", label: "Speech to text (fast)", hint: "Lower latency" },
];

/** Providers holding an account credential, and the shape that credential takes. */
const CREDENTIAL_PROVIDERS: Array<{
  provider: string;
  label: string;
  kind: "key" | "json";
  usage: string;
}> = [
  {
    provider: "openrouter",
    label: "OpenRouter",
    kind: "key",
    usage: "Every chat model",
  },
  {
    provider: "openai",
    label: "OpenAI",
    kind: "key",
    usage: "Voice: speech synthesis and transcription",
  },
  {
    provider: "gemini",
    label: "Google Vertex",
    kind: "json",
    usage: "Embeddings, OCR, chat fallback",
  },
  {
    provider: "google",
    label: "Google Cloud",
    kind: "json",
    usage: "Voice fallback, language detection",
  },
];

const money = (v: number | null | undefined) => (v == null ? "—" : `$${v}`);

const HEAD_ROW =
  "border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500";

const AiEngineModels: React.FC<{ base: string }> = ({ base }) => {
  const [models, setModels] = useState<Model[]>([]);
  const [tiers, setTiers] = useState<Tiers>({});
  const [chatScenarios, setChatScenarios] = useState<ChatScenario[]>([]);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [pingResults, setPingResults] = useState<PingReport | null>(null);
  const [pickerScenario, setPickerScenario] = useState<string | null>(null);
  const [credentialTarget, setCredentialTarget] = useState<string | null>(null);
  const [credentialValue, setCredentialValue] = useState("");
  const [savingCredential, setSavingCredential] = useState(false);
  const [addingVoice, setAddingVoice] = useState(false);
  const [voiceDraft, setVoiceDraft] = useState({
    company: "openai",
    model: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await HttpService.get<{
        element: { models: Model[]; tiers: Tiers };
      }>(`${base}/models`);
      setModels(res.data?.element?.models ?? []);
      setTiers(res.data?.element?.tiers ?? {});

      HttpService.get<{ element: { scenarios: ChatScenario[] } }>(
        `${base}/chat`
      )
        .then((r) => setChatScenarios(r.data?.element?.scenarios ?? []))
        .catch(() => setChatScenarios([]));
      HttpService.get<{ element: { credentials: Credential[] } }>(
        `${base}/credentials`
      )
        .then((r) => setCredentials(r.data?.element?.credentials ?? []))
        .catch(() => setCredentials([]));
    } catch {
      toast.error("Could not read the AI Engine configuration");
      setModels([]);
      setTiers({});
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  // Every row in the catalogue is live: a model that is no longer bound is deleted, not disabled.
  const voiceModels = models.filter((m) => m.scope === "voice");
  const embeddingModel = models.find((m) => m.scope === "embedding");
  const chatTiers = tiers.default ?? {};
  const voiceTiers = tiers.voice ?? {};

  const saveTier = async (scope: string, role: string, modelName: string) => {
    try {
      await HttpService.put(`${base}/tiers`, {
        scope,
        mapping: { [role]: modelName },
      });
      toast.success("Configuration updated");
      setTiers((prev) => ({
        ...prev,
        [scope]: { ...(prev[scope] || {}), [role]: modelName },
      }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Could not save");
    }
  };

  const testAllLlms = async () => {
    setPinging(true);
    try {
      const res = await HttpService.post<{ element: PingReport }>(
        `${base}/models/ping`,
        {}
      );
      setPingResults(res.data?.element ?? null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Health check failed");
    } finally {
      setPinging(false);
    }
  };

  const saveCredential = async () => {
    if (!credentialTarget || !credentialValue.trim()) return;
    setSavingCredential(true);
    try {
      await HttpService.put(`${base}/credentials/${credentialTarget}`, {
        api_key: credentialValue.trim(),
      });
      toast.success("Credential saved");
      setCredentialTarget(null);
      setCredentialValue("");
      load();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Could not save the credential"
      );
    } finally {
      setSavingCredential(false);
    }
  };

  const addVoiceModel = async () => {
    if (!voiceDraft.model.trim()) return;
    try {
      await HttpService.post(`${base}/models`, {
        company: voiceDraft.company,
        model: voiceDraft.model.trim(),
        scope: "voice",
        params: {},
      });
      toast.success("Voice model added");
      setAddingVoice(false);
      setVoiceDraft({ company: "openai", model: "" });
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Could not add the model");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {/* ── 1 · Chat ──────────────────────────────────────────────────────── */}
      <section>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Chat</h3>
            <p className="mt-0.5 max-w-3xl text-xs text-gray-500">
              The app asks for a role, never for a model. Each role maps to one
              model and, when pinned, to one precise OpenRouter endpoint. The
              same model can cost several times more on another endpoint.
            </p>
          </div>
          <CustomButton
            variant="secondary"
            icon="documentSearch"
            title="Test all chat models"
            onClick={testAllLlms}
            isLoading={pinging}
          />
        </div>

        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className={HEAD_ROW}>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Endpoint</th>
                <th className="px-3 py-2 text-right font-medium">
                  $ per 1M (in / out)
                </th>
                <th className="px-3 py-2 font-medium">Fallback</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {CHAT_ROLES.map(({ key, label, hint }) => {
                const detail = chatScenarios.find((c) => c.scenario === key);
                return (
                  <tr key={key}>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-xs text-gray-400">{hint}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-xs text-gray-700">
                        {chatTiers[key] ?? (
                          <span className="text-amber-600">not configured</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setPickerScenario(key)}
                        className="mt-0.5 text-xs text-blue-600 hover:underline"
                      >
                        Change model / endpoint
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {detail?.pinned_upstream ?? (
                        <span
                          className="text-amber-600"
                          title="No endpoint pinned: OpenRouter picks one, and the price is not guaranteed"
                        >
                          not pinned
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-xs tabular-nums text-gray-500">
                      {detail?.price_in_per_1m != null
                        ? `${money(detail.price_in_per_1m)} / ${money(
                            detail.price_out_per_1m
                          )}`
                        : "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {detail?.fallback ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pingResults && (
          <div className="mt-3 rounded-lg border border-gray-200 p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-sm font-semibold text-gray-900">
                Health check
                <StatusBadge
                  color={pingResults.all_ok ? "success" : "warning"}
                  size="xs"
                  className="ml-2 inline-block"
                >
                  {pingResults.ok_count}/{pingResults.total} OK
                </StatusBadge>
              </div>
              <button
                type="button"
                onClick={() => setPingResults(null)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Dismiss
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {pingResults.results.map((r) => (
                    <tr key={`${r.provider}/${r.model}`}>
                      <td className="whitespace-nowrap py-1.5 pr-3 text-xs text-gray-500">
                        {r.provider}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-gray-900">
                        {r.model}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <StatusBadge
                          color={r.ok ? "success" : "failure"}
                          size="xs"
                        >
                          {r.ok ? "OK" : "KO"}
                        </StatusBadge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-xs tabular-nums text-gray-500">
                        {r.ok ? `${r.latency_ms} ms` : ""}
                      </td>
                      <td className="py-1.5 pl-3 text-xs text-gray-500">
                        {r.error ?? ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ── 2 · Voice ─────────────────────────────────────────────────────── */}
      <section className="mt-8">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">Voice</h3>
            <p className="mt-0.5 max-w-3xl text-xs text-gray-500">
              Same shape as chat: four roles, each bound to a model. Quality
              trades latency for a more natural result; fast does the opposite.
            </p>
          </div>
          <CustomButton
            variant="secondary"
            icon="plus"
            title="Add a voice model"
            onClick={() => setAddingVoice(true)}
          />
        </div>

        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className={HEAD_ROW}>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Model</th>
                <th className="px-3 py-2 font-medium">Provider</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {VOICE_ROLES.map(({ key, label, hint }) => {
                const bound = voiceTiers[key];
                const boundModel = voiceModels.find((m) => m.model === bound);
                return (
                  <tr key={key}>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-xs text-gray-400">{hint}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Select
                        id={`voice-${key}`}
                        sizing="sm"
                        value={bound ?? ""}
                        onChange={(e) => saveTier("voice", key, e.target.value)}
                      >
                        {!bound && <option value="">not configured</option>}
                        {voiceModels.map((m) => (
                          <option key={m.model} value={m.model}>
                            {m.model}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500">
                      {boundModel?.company ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── 3 · Embedding (read-only, on purpose) ─────────────────────────── */}
      <section className="mt-8">
        <h3 className="text-sm font-semibold text-gray-900">Embedding</h3>
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-mono text-sm text-gray-900">
              {embeddingModel?.model ?? "—"}
            </span>
            {embeddingModel?.params?.dim ? (
              <span className="text-xs text-gray-500">
                {String(embeddingModel.params.dim)} dimensions
              </span>
            ) : null}
            <StatusBadge color="gray" size="xs">
              not changeable
            </StatusBadge>
          </div>
          <p className="mt-1.5 max-w-3xl text-xs text-gray-500">
            Every vector stored for every document and every searchable key was
            produced by this model. Switching it would not degrade search. It
            would silently make it wrong: new queries would be compared against
            vectors computed in a different space. Changing it is a re-embedding
            project, so the engine refuses the change rather than offering it
            here.
          </p>
        </div>
      </section>

      {/* ── 4 · Credentials ───────────────────────────────────────────────── */}
      <section className="mt-8">
        <h3 className="text-sm font-semibold text-gray-900">Credentials</h3>
        <p className="mt-0.5 max-w-3xl text-xs text-gray-500">
          One per provider account, not per model. Write-only: a key is never
          sent back to this page.
        </p>
        <div className="mt-2 overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className={HEAD_ROW}>
                <th className="px-3 py-2 font-medium">Provider</th>
                <th className="px-3 py-2 font-medium">Used for</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="w-px px-3 py-2 text-right font-medium">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {CREDENTIAL_PROVIDERS.map(({ provider, label, usage }) => {
                const cred = credentials.find((c) => c.provider === provider);
                return (
                  <tr key={provider}>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-gray-900">
                      {label}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{usage}</td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <StatusBadge
                        color={cred?.configured ? "success" : "warning"}
                        size="xs"
                      >
                        {cred?.configured ? "configured" : "missing"}
                      </StatusBadge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setCredentialTarget(provider);
                          setCredentialValue("");
                        }}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {cred?.configured ? "Rotate" : "Set"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── modals ────────────────────────────────────────────────────────── */}
      <ChatUpstreamPicker
        base={base}
        scenario={pickerScenario}
        scenarioLabel={
          CHAT_ROLES.find((r) => r.key === pickerScenario)?.label ?? ""
        }
        currentModel={pickerScenario ? chatTiers[pickerScenario] : undefined}
        currentUpstream={
          pickerScenario
            ? chatScenarios.find((c) => c.scenario === pickerScenario)
                ?.pinned_upstream ?? null
            : null
        }
        onClose={() => setPickerScenario(null)}
        onSaved={load}
      />

      <ModalShell
        show={Boolean(credentialTarget)}
        onClose={() => setCredentialTarget(null)}
        size="lg"
        title={`${
          CREDENTIAL_PROVIDERS.find((p) => p.provider === credentialTarget)
            ?.label ?? ""
        } credential`}
        subtitle="Stored once for the whole provider account, and never displayed again."
        footer={
          <>
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setCredentialTarget(null)}
            />
            <CustomButton
              variant="primary"
              title="Save"
              onClick={saveCredential}
              isLoading={savingCredential}
              disabled={!credentialValue.trim()}
            />
          </>
        }
      >
        {CREDENTIAL_PROVIDERS.find((p) => p.provider === credentialTarget)
          ?.kind === "json" ? (
          <>
            <FieldLabel value="Service-account JSON" />
            <Textarea
              rows={8}
              className="w-full font-mono text-xs"
              placeholder='{ "type": "service_account", "project_id": "…", "private_key": "…" }'
              value={credentialValue}
              onChange={(e) => setCredentialValue(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              The whole file, as downloaded from Google Cloud. It is validated
              before being stored.
            </p>
          </>
        ) : (
          <>
            <FieldLabel value="API key" />
            <TextInput
              type="password"
              className="w-full"
              placeholder="sk-…"
              value={credentialValue}
              onChange={(e) => setCredentialValue(e.target.value)}
            />
          </>
        )}
      </ModalShell>

      <ModalShell
        show={addingVoice}
        onClose={() => setAddingVoice(false)}
        size="md"
        title="Add a voice model"
        subtitle="Makes a new model id available to the roles above."
        footer={
          <>
            <CustomButton
              variant="secondary"
              title="Cancel"
              onClick={() => setAddingVoice(false)}
            />
            <CustomButton
              variant="primary"
              title="Add"
              onClick={addVoiceModel}
              disabled={!voiceDraft.model.trim()}
            />
          </>
        }
      >
        <div className="grid gap-3">
          <div>
            <FieldLabel value="Provider" />
            <Select
              value={voiceDraft.company}
              onChange={(e) =>
                setVoiceDraft((d) => ({ ...d, company: e.target.value }))
              }
            >
              <option value="openai">OpenAI</option>
              <option value="google">Google Cloud</option>
            </Select>
          </div>
          <div>
            <FieldLabel value="Model id" />
            <TextInput
              className="w-full"
              placeholder="gpt-4o-mini-tts"
              value={voiceDraft.model}
              onChange={(e) =>
                setVoiceDraft((d) => ({ ...d, model: e.target.value }))
              }
            />
          </div>
        </div>
      </ModalShell>
    </div>
  );
};

export default AiEngineModels;
